import type {
  BarcodeGrantControlKind,
  StaffOperationsSnapshot,
  StaffOperationsView as StaffOperationsViewKind,
  StaffVisitSummary,
  VisitPacketCodeFormat,
  VisitTransitionRequest,
} from "@whitehaven/contracts";
import {
  Card,
  ContentState,
  Dialog,
  StatusMessage,
  StatusPill,
} from "@whitehaven/ui";
import { useMemo, useRef, useState } from "react";
import type { StaffVisitPacketBundle } from "./staff-operations-gateway";
import {
  operationForStatus,
  selectCurrentAndNextStaffVisits,
  staffOperationsLinks,
} from "./staff-operations-model";
import { StaffVisitPacketPrintView } from "./StaffVisitPacketPrintView";

export interface StaffOperationsFeedback {
  readonly tone: "positive" | "critical";
  readonly message: string;
}

interface StaffOperationsViewProps {
  readonly view: StaffOperationsViewKind;
  readonly snapshot: StaffOperationsSnapshot;
  readonly canMutate: boolean;
  readonly canPrint: boolean;
  readonly canManageCodes: boolean;
  readonly syntheticPreview: boolean;
  readonly assignedLocationIds: readonly string[];
  readonly selectedLocationId: string;
  readonly selectedDate: string;
  readonly selectedAppointmentId: string | null;
  readonly pendingVisitId: string | null;
  readonly busy: boolean;
  readonly feedback: StaffOperationsFeedback | null;
  readonly printBundles: readonly StaffVisitPacketBundle[];
  readonly onTransition: (
    visit: StaffVisitSummary,
    kind: VisitTransitionRequest["kind"],
  ) => void;
  readonly onPrint: (
    visitIds: readonly string[],
    format: VisitPacketCodeFormat,
  ) => void;
  readonly onOpenPrintDialog: () => void;
  readonly onBarcodeControl: (
    bundle: StaffVisitPacketBundle,
    kind: BarcodeGrantControlKind,
  ) => void;
  readonly onFilterChange: (next: {
    readonly locationId: string;
    readonly date: string;
    readonly appointmentId: string | null;
  }) => void;
}

function formatTime(timestamp: string, timezone: string): string {
  const value = new Date(timestamp);
  try {
    return new Intl.DateTimeFormat("en-US", {
      timeZone: timezone,
      hour: "numeric",
      minute: "2-digit",
      timeZoneName: "short",
    }).format(value);
  } catch {
    return "Time unavailable";
  }
}

function statusTone(
  status: StaffVisitSummary["status"],
): "positive" | "warning" | "neutral" | "critical" {
  if (["CHECKED_IN", "IN_PROGRESS"].includes(status)) return "positive";
  if (["COMPLETED", "CHECKOUT_PENDING"].includes(status)) return "warning";
  if (["NO_SHOW", "CANCELLED"].includes(status)) return "critical";
  return "neutral";
}

function VisitDetails({ visit }: { readonly visit: StaffVisitSummary }) {
  const room = visit.resources.find((resource) => resource.kind === "ROOM");
  const station = visit.resources.find(
    (resource) => resource.kind === "STATION",
  );
  return (
    <>
      <dl className="staff-visit-details">
        <div>
          <dt>Time</dt>
          <dd>
            {formatTime(visit.startsAt, visit.timezone)} ·{" "}
            {visit.durationMinutes} minutes
          </dd>
        </div>
        <div>
          <dt>Service / modality</dt>
          <dd>
            {visit.serviceLabel} · {visit.modalityLabels.join(", ")}
          </dd>
        </div>
        <div>
          <dt>Room / station</dt>
          <dd>
            {[room?.label, station?.label].filter(Boolean).join(" · ") ||
              "Unassigned"}
          </dd>
        </div>
        <div>
          <dt>Readiness</dt>
          <dd>
            Forms {visit.readiness.forms.replaceAll("_", " ")} · Consent{" "}
            {visit.readiness.consent.replaceAll("_", " ")}
          </dd>
        </div>
        <div>
          <dt>Setup</dt>
          <dd>{visit.setupRequirements.join(", ") || "None listed"}</dd>
        </div>
        <div>
          <dt>Equipment</dt>
          <dd>{visit.equipmentRequirements.join(", ") || "None listed"}</dd>
        </div>
        <div>
          <dt>Checkout readiness</dt>
          <dd>{visit.checkoutIndicator.replaceAll("_", " ")}</dd>
        </div>
      </dl>
      {visit.operationalAlerts.length > 0 && (
        <p className="staff-visit-alert">
          {visit.operationalAlerts.join(" · ")}
        </p>
      )}
    </>
  );
}

function VisitActions({
  visit,
  canMutate,
  pending,
  now,
  onTransition,
}: {
  readonly visit: StaffVisitSummary;
  readonly canMutate: boolean;
  readonly pending: boolean;
  readonly now: string;
  readonly onTransition: StaffOperationsViewProps["onTransition"];
}) {
  const [confirmNoShow, setConfirmNoShow] = useState(false);
  const cancelRef = useRef<HTMLButtonElement>(null);
  const operation = operationForStatus(visit.status);
  const noShowAllowed = visit.status === "SCHEDULED" && visit.startsAt <= now;
  if (!canMutate || (!operation && visit.status !== "SCHEDULED")) return null;
  return (
    <div className="staff-visit-actions">
      {operation && (
        <button
          type="button"
          disabled={pending}
          onClick={() => onTransition(visit, operation)}
        >
          {operation.replaceAll("_", " ")}
        </button>
      )}
      {visit.status === "SCHEDULED" && (
        <>
          <button
            type="button"
            disabled={pending || !noShowAllowed}
            aria-describedby={
              noShowAllowed ? undefined : `no-show-${visit.visitId}-reason`
            }
            onClick={() => setConfirmNoShow(true)}
          >
            Record no-show
          </button>
          {!noShowAllowed && (
            <span id={`no-show-${visit.visitId}-reason`}>
              No-show becomes available when the confirmed appointment starts.
            </span>
          )}
          <Dialog
            id={`no-show-${visit.visitId}`}
            open={confirmNoShow}
            title="Confirm no-show"
            description={`Record ${visit.guestDisplayName} as a no-show only after the scheduled start time and a staff attendance check.`}
            initialFocusRef={cancelRef}
            onRequestClose={() => setConfirmNoShow(false)}
            actions={
              <>
                <button
                  type="button"
                  ref={cancelRef}
                  onClick={() => setConfirmNoShow(false)}
                >
                  Keep scheduled
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setConfirmNoShow(false);
                    onTransition(visit, "NO_SHOW");
                  }}
                >
                  Confirm no-show
                </button>
              </>
            }
          >
            This creates an immutable operational event and updates the
            appointment-owned status.
          </Dialog>
        </>
      )}
    </div>
  );
}

function VisitCard({
  visit,
  canMutate,
  pending,
  headingLevel,
  now,
  onTransition,
}: {
  readonly visit: StaffVisitSummary;
  readonly canMutate: boolean;
  readonly pending: boolean;
  readonly headingLevel: 2 | 3;
  readonly now: string;
  readonly onTransition: StaffOperationsViewProps["onTransition"];
}) {
  return (
    <Card
      headingLevel={headingLevel}
      eyebrow={formatTime(visit.startsAt, visit.timezone)}
      title={visit.guestDisplayName}
      description={`${visit.serviceLabel} · ${visit.durationMinutes} minutes`}
      footer={`Visit revision ${visit.revision}`}
      className="staff-visit-card"
    >
      <StatusPill tone={statusTone(visit.status)}>
        {visit.status.replaceAll("_", " ")}
      </StatusPill>
      <VisitDetails visit={visit} />
      <VisitActions
        visit={visit}
        canMutate={canMutate}
        pending={pending}
        now={now}
        onTransition={onTransition}
      />
    </Card>
  );
}

function Timeline({
  visits,
  now,
  ...props
}: {
  readonly visits: readonly StaffVisitSummary[];
  readonly now: string;
  readonly canMutate: boolean;
  readonly pendingVisitId: string | null;
  readonly onTransition: StaffOperationsViewProps["onTransition"];
}) {
  return (
    <ol className="staff-timeline">
      {visits.map((visit) => (
        <li key={visit.visitId}>
          <time dateTime={visit.startsAt}>
            {formatTime(visit.startsAt, visit.timezone)}
          </time>
          <VisitCard
            visit={visit}
            canMutate={props.canMutate}
            pending={props.pendingVisitId !== null}
            headingLevel={2}
            now={now}
            onTransition={props.onTransition}
          />
        </li>
      ))}
    </ol>
  );
}

function CompactList({
  visits,
}: {
  readonly visits: readonly StaffVisitSummary[];
}) {
  return (
    <div
      className="staff-visit-table-wrap"
      role="region"
      aria-label="Authorized visit table"
      tabIndex={0}
    >
      <table className="staff-visit-table">
        <caption>Authorized visits for this location and date</caption>
        <thead>
          <tr>
            <th scope="col">Time</th>
            <th scope="col">Guest</th>
            <th scope="col">Service</th>
            <th scope="col">Resource</th>
            <th scope="col">Readiness</th>
            <th scope="col">Status</th>
          </tr>
        </thead>
        <tbody>
          {visits.map((visit) => (
            <tr key={visit.visitId}>
              <td>{formatTime(visit.startsAt, visit.timezone)}</td>
              <th scope="row">{visit.guestDisplayName}</th>
              <td>{visit.serviceLabel}</td>
              <td>
                {visit.resources.map((resource) => resource.label).join(", ")}
              </td>
              <td>Forms {visit.readiness.forms.replaceAll("_", " ")}</td>
              <td>{visit.status.replaceAll("_", " ")}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function ResourceBoard({
  visits,
}: {
  readonly visits: readonly StaffVisitSummary[];
}) {
  const groups = new Map<
    string,
    { readonly label: string; readonly visits: StaffVisitSummary[] }
  >();
  for (const visit of visits) {
    const resources = visit.resources.filter((resource) =>
      ["ROOM", "STATION"].includes(resource.kind),
    );
    for (const resource of resources.length > 0
      ? resources
      : [
          {
            id: "unassigned-resource",
            kind: "ROOM" as const,
            label: "Unassigned",
          },
        ]) {
      const group = groups.get(resource.id) ?? {
        label: resource.label,
        visits: [],
      };
      group.visits.push(visit);
      groups.set(resource.id, group);
    }
  }
  return (
    <>
      <div className="staff-resource-board" aria-label="Resource board">
        {[...groups.entries()].map(([resourceId, group]) => (
          <section key={resourceId} aria-labelledby={`resource-${resourceId}`}>
            <h2 id={`resource-${resourceId}`}>{group.label}</h2>
            {group.visits.map((visit) => (
              <p key={visit.visitId}>
                <strong>{formatTime(visit.startsAt, visit.timezone)}</strong>{" "}
                {visit.guestDisplayName} · {visit.serviceLabel}
              </p>
            ))}
          </section>
        ))}
      </div>
      <CompactList visits={visits} />
    </>
  );
}

function PrintQueue({
  snapshot,
  bundles,
  canPrint,
  canManageCodes,
  busy,
  onPrint,
  onOpenPrintDialog,
  onBarcodeControl,
}: {
  readonly snapshot: StaffOperationsSnapshot;
  readonly bundles: readonly StaffVisitPacketBundle[];
  readonly canPrint: boolean;
  readonly canManageCodes: boolean;
  readonly busy: boolean;
  readonly onPrint: StaffOperationsViewProps["onPrint"];
  readonly onOpenPrintDialog: StaffOperationsViewProps["onOpenPrintDialog"];
  readonly onBarcodeControl: StaffOperationsViewProps["onBarcodeControl"];
}) {
  const [selected, setSelected] = useState<readonly string[]>([]);
  const [format, setFormat] = useState<VisitPacketCodeFormat>("QR");
  const [pendingCodeAction, setPendingCodeAction] = useState<{
    readonly bundle: StaffVisitPacketBundle;
    readonly kind: BarcodeGrantControlKind;
  } | null>(null);
  const cancelCodeActionRef = useRef<HTMLButtonElement>(null);
  const eligible = snapshot.visits.filter(
    (visit) => !["CANCELLED", "NO_SHOW"].includes(visit.status),
  );
  return (
    <>
      <section className="staff-print-queue portal-screen-only">
        <h2>Visit packet print queue</h2>
        <p>
          Select one visit or a bounded location/day batch. Opening the print
          dialog creates an audit intent; the browser cannot attest that a
          physical page was produced.
        </p>
        <fieldset disabled={busy}>
          <legend>Visits</legend>
          {eligible.map((visit) => (
            <label key={visit.visitId}>
              <input
                type="checkbox"
                checked={selected.includes(visit.visitId)}
                disabled={
                  busy ||
                  (selected.length >= 100 && !selected.includes(visit.visitId))
                }
                onChange={(event) =>
                  setSelected((current) =>
                    event.currentTarget.checked
                      ? [...current, visit.visitId]
                      : current.filter((id) => id !== visit.visitId),
                  )
                }
              />
              {formatTime(visit.startsAt, visit.timezone)} ·{" "}
              {visit.guestDisplayName}
            </label>
          ))}
        </fieldset>
        <p>{selected.length} of 100 maximum packets selected.</p>
        <label>
          Code format
          <select
            value={format}
            disabled={busy}
            onChange={(event) =>
              setFormat(event.currentTarget.value as VisitPacketCodeFormat)
            }
          >
            <option value="QR">QR Code (default)</option>
            <option value="DATA_MATRIX">Data Matrix</option>
          </select>
        </label>
        <button
          type="button"
          disabled={busy || !canPrint || selected.length === 0}
          onClick={() => onPrint(selected, format)}
        >
          Prepare {selected.length === 1 ? "packet" : "batch"}
        </button>
        {bundles.length > 0 && (
          <button
            type="button"
            disabled={busy || !canPrint}
            onClick={onOpenPrintDialog}
          >
            Open print dialog
          </button>
        )}
        {bundles.length > 0 && (
          <section aria-labelledby="prepared-visit-codes">
            <h3 id="prepared-visit-codes">Prepared code controls</h3>
            <p>
              Regeneration revokes the current code and prepares a replacement;
              its next print-dialog open is audited. Revocation immediately
              removes the code from this print set.
            </p>
            <ul className="staff-barcode-controls">
              {bundles.map((bundle) => (
                <li key={bundle.grantId}>
                  <span>
                    {bundle.packet.guestDisplayName} / expires{" "}
                    {bundle.grantExpiresAt}
                  </span>
                  <button
                    type="button"
                    disabled={busy || !canManageCodes}
                    onClick={() =>
                      setPendingCodeAction({ bundle, kind: "REGENERATE" })
                    }
                  >
                    Regenerate code
                  </button>
                  <button
                    type="button"
                    disabled={busy || !canManageCodes}
                    onClick={() =>
                      setPendingCodeAction({ bundle, kind: "REVOKE" })
                    }
                  >
                    Revoke code
                  </button>
                </li>
              ))}
            </ul>
            <Dialog
              id="staff-barcode-control-confirmation"
              open={pendingCodeAction !== null}
              title={
                pendingCodeAction?.kind === "REGENERATE"
                  ? "Confirm code regeneration"
                  : "Confirm code revocation"
              }
              description={
                pendingCodeAction
                  ? `${pendingCodeAction.kind === "REGENERATE" ? "Regenerate" : "Revoke"} the visit code for ${pendingCodeAction.bundle.packet.guestDisplayName}? The current code will stop resolving immediately.`
                  : ""
              }
              initialFocusRef={cancelCodeActionRef}
              onRequestClose={() => setPendingCodeAction(null)}
              actions={
                <>
                  <button
                    ref={cancelCodeActionRef}
                    type="button"
                    onClick={() => setPendingCodeAction(null)}
                  >
                    Keep current code
                  </button>
                  <button
                    type="button"
                    disabled={busy}
                    onClick={() => {
                      if (!pendingCodeAction) return;
                      const action = pendingCodeAction;
                      setPendingCodeAction(null);
                      onBarcodeControl(action.bundle, action.kind);
                    }}
                  >
                    {pendingCodeAction?.kind === "REGENERATE"
                      ? "Regenerate code"
                      : "Revoke code"}
                  </button>
                </>
              }
            >
              This one-way control is recorded against the visit-scoped grant.
            </Dialog>
          </section>
        )}
      </section>
      {bundles.some((bundle) => bundle.printAudit !== null) && (
        <div className="portal-print-only">
          <StaffVisitPacketPrintView bundles={bundles} />
        </div>
      )}
    </>
  );
}

export function StaffOperationsView({
  view,
  snapshot,
  canMutate,
  canPrint,
  canManageCodes,
  syntheticPreview,
  assignedLocationIds,
  selectedLocationId,
  selectedDate,
  selectedAppointmentId,
  pendingVisitId,
  busy,
  feedback,
  printBundles,
  onTransition,
  onPrint,
  onOpenPrintDialog,
  onBarcodeControl,
  onFilterChange,
}: StaffOperationsViewProps) {
  const selectedVisits = useMemo(() => {
    if (view === "CHECK_IN_QUEUE") {
      return snapshot.visits.filter((visit) => visit.status === "SCHEDULED");
    }
    if (view === "CHECKOUT_QUEUE") {
      return snapshot.visits.filter((visit) =>
        ["COMPLETED", "CHECKOUT_PENDING"].includes(visit.status),
      );
    }
    return snapshot.visits;
  }, [snapshot.visits, view]);
  const { current, next } = selectCurrentAndNextStaffVisits(
    snapshot.visits,
    snapshot.generatedAt,
  );

  return (
    <section className="portal-page staff-operations-page" aria-busy={busy}>
      <header className="portal-page__header portal-screen-only">
        <div>
          <p className="portal-kicker">Staff daily operations</p>
          <h1 id="portal-title" tabIndex={-1} data-main-focus data-route-focus>
            {view === "HOME"
              ? "Staff home"
              : view.replaceAll("_", " ").toLowerCase()}
          </h1>
          <p>
            {snapshot.date} · {snapshot.locationLabel} ·{" "}
            {snapshot.visits.length} authorized visits
          </p>
        </div>
        {syntheticPreview && (
          <StatusPill tone="warning">Synthetic preview data</StatusPill>
        )}
      </header>
      <nav
        className="staff-operations-nav portal-screen-only"
        aria-label="Staff operations views"
      >
        {staffOperationsLinks.map((link) => (
          <a
            href={link.href}
            aria-current={link.view === view ? "page" : undefined}
            key={link.href}
          >
            {link.label}
          </a>
        ))}
      </nav>
      <p
        className="staff-operations-working portal-screen-only"
        role="status"
        aria-live="polite"
        aria-atomic="true"
      >
        {busy ? "Working..." : ""}
      </p>
      <fieldset className="staff-operations-filters portal-screen-only">
        <legend>Assigned visit filters</legend>
        <label>
          Date
          <input
            type="date"
            value={selectedDate}
            disabled={busy}
            onChange={(event) =>
              onFilterChange({
                locationId: selectedLocationId,
                date: event.currentTarget.value,
                appointmentId: null,
              })
            }
          />
        </label>
        <label>
          Assigned location
          <select
            value={selectedLocationId}
            disabled={busy}
            onChange={(event) =>
              onFilterChange({
                locationId: event.currentTarget.value,
                date: selectedDate,
                appointmentId: null,
              })
            }
          >
            {assignedLocationIds.map((id, index) => (
              <option value={id} key={id}>
                {id === snapshot.locationId
                  ? snapshot.locationLabel
                  : `Assigned location ${index + 1}`}
              </option>
            ))}
          </select>
        </label>
        <label>
          Appointment
          <select
            value={selectedAppointmentId ?? ""}
            disabled={busy}
            onChange={(event) =>
              onFilterChange({
                locationId: selectedLocationId,
                date: selectedDate,
                appointmentId: event.currentTarget.value || null,
              })
            }
          >
            <option value="">All authorized appointments</option>
            {snapshot.visits.map((visit) => (
              <option value={visit.appointmentId} key={visit.appointmentId}>
                {formatTime(visit.startsAt, visit.timezone)} /{" "}
                {visit.guestDisplayName}
              </option>
            ))}
          </select>
        </label>
      </fieldset>
      {feedback && (
        <div id="staff-operations-feedback" tabIndex={-1}>
          <StatusMessage tone={feedback.tone} title={feedback.message} />
        </div>
      )}
      {view === "HOME" && (
        <div className="staff-current-next">
          <section>
            <h2>Current guest</h2>
            {current ? (
              <VisitCard
                visit={current}
                canMutate={canMutate}
                pending={busy || pendingVisitId !== null}
                headingLevel={3}
                now={snapshot.generatedAt}
                onTransition={onTransition}
              />
            ) : (
              <p>No current visit.</p>
            )}
          </section>
          <section>
            <h2>Next guest</h2>
            {next ? (
              <VisitCard
                visit={next}
                canMutate={canMutate}
                pending={busy || pendingVisitId !== null}
                headingLevel={3}
                now={snapshot.generatedAt}
                onTransition={onTransition}
              />
            ) : (
              <p>No later assigned visit.</p>
            )}
          </section>
        </div>
      )}
      {view === "TIMELINE" &&
        (selectedVisits.length > 0 ? (
          <Timeline
            visits={selectedVisits}
            now={snapshot.generatedAt}
            canMutate={canMutate}
            pendingVisitId={busy ? "busy" : pendingVisitId}
            onTransition={onTransition}
          />
        ) : (
          <ContentState kind="empty" title="No visits on this timeline" />
        ))}
      {view === "LIST" &&
        (selectedVisits.length > 0 ? (
          <CompactList visits={selectedVisits} />
        ) : (
          <ContentState kind="empty" title="No visits in this list" />
        ))}
      {view === "RESOURCE_BOARD" &&
        (selectedVisits.length > 0 ? (
          <ResourceBoard visits={selectedVisits} />
        ) : (
          <ContentState kind="empty" title="No visits on this resource board" />
        ))}
      {(view === "CHECK_IN_QUEUE" || view === "CHECKOUT_QUEUE") &&
        (selectedVisits.length > 0 ? (
          <div className="staff-visit-grid">
            {selectedVisits.map((visit) => (
              <VisitCard
                key={visit.visitId}
                visit={visit}
                canMutate={canMutate}
                pending={busy || pendingVisitId !== null}
                headingLevel={2}
                now={snapshot.generatedAt}
                onTransition={onTransition}
              />
            ))}
          </div>
        ) : (
          <ContentState
            kind="empty"
            title={
              view === "CHECK_IN_QUEUE"
                ? "No guests are waiting for check-in"
                : "No visits are waiting for checkout"
            }
            live={false}
          />
        ))}
      {view === "PRINT_QUEUE" && (
        <PrintQueue
          snapshot={snapshot}
          bundles={printBundles}
          canPrint={canPrint}
          canManageCodes={canManageCodes}
          busy={busy}
          onPrint={onPrint}
          onOpenPrintDialog={onOpenPrintDialog}
          onBarcodeControl={onBarcodeControl}
        />
      )}
      {view !== "PRINT_QUEUE" && snapshot.events.length > 0 && (
        <section className="staff-event-timeline">
          <h2>Visit event timeline</h2>
          <ol>
            {snapshot.events.map((event) => (
              <li key={event.id}>
                {(() => {
                  const visit = snapshot.visits.find(
                    (candidate) => candidate.visitId === event.visitId,
                  );
                  return (
                    <>
                      <time dateTime={event.occurredAt}>
                        {visit
                          ? formatTime(event.occurredAt, visit.timezone)
                          : "Time unavailable"}
                      </time>{" "}
                      <strong>
                        {visit?.guestDisplayName ?? "Authorized visit"}
                      </strong>
                      : {event.fromStatus.replaceAll("_", " ")} to{" "}
                      {event.toStatus.replaceAll("_", " ")} (
                      {event.kind.replaceAll("_", " ").toLowerCase()})
                    </>
                  );
                })()}
              </li>
            ))}
          </ol>
        </section>
      )}
    </section>
  );
}
